import { useMemo } from 'react';

import { useCurrentUser } from '@/features/identity';
import { resolveLabelNarrowing } from '@/features/labels';
import { listWorkspaceTasks, type WorkspaceTaskRow } from '@/features/tasks';
import type { WorkspaceAggregate } from '@/lib/api/generated/model';

import { matchesListView, type TaskListView } from './listView';
import { orderTaskRows } from './taskSearch';

export interface TaskListRows {
  /** Every task in the workspace, as rows. What the tiles count. */
  allRows: WorkspaceTaskRow[];
  /** The view with labels resolved against this workspace's vocabulary. */
  resolved: TaskListView;
  /** The rows the view keeps, in its order. What the list draws. */
  rows: WorkspaceTaskRow[];
  /** The signed-in member's id, once known. */
  me: string | undefined;
}

const NONE: WorkspaceTaskRow[] = [];

/**
 * The Tasks page's rows, derived once for everything on the page that reads
 * them (spec 113 R2).
 *
 * Lifted out of the list because the count moved into the filter row, which
 * sits *above* the list: deriving the rows in both places would repeat the
 * most expensive walk on the page. The memos and their dependencies are the
 * ones the list had — see the comments on each.
 *
 * `now` is the time the workspace was read, not the wall clock: a hook must be
 * pure (the list could read `Date.now()` only because the virtualiser keeps
 * the React Compiler out of it). The read time moves with every poll, so the
 * list is at most one poll old while anything is live — what it was before.
 */
export function useTaskListRows(
  workspace: WorkspaceAggregate | null | undefined,
  view: TaskListView,
  now: number,
): TaskListRows {
  const workflows = workspace?.workflows;
  const tasks = workspace?.tasks;
  const registry = workspace?.labels;

  // Derived the way `BoardPage` memoises `buildBoard`: this walk builds every
  // workflow's board, so repeating it on an unrelated render is the one
  // genuinely expensive mistake available here.
  const allRows = useMemo(
    () => (workflows && tasks ? listWorkspaceTasks(workflows, tasks) : NONE),
    [workflows, tasks],
  );

  // Resolved against the vocabulary before anything reads it, so a link naming
  // a label this workspace no longer has is ignored rather than emptying the
  // page — the rule 067 settled, reused rather than restated.
  const resolved = useMemo(
    () => ({
      ...view,
      labels: resolveLabelNarrowing(view.labels, registry ?? []),
    }),
    [view, registry],
  );

  // "Me", for Assigned to me and Created by me. Until the session is read, a
  // "me" filter narrows nothing rather than everything (spec 112 edge case).
  const me = useCurrentUser().data?.id;

  // Filtered *before* ordering and before the virtualiser measures, so the
  // scrollbar reflects the filtered length rather than the whole workspace.
  // Time ranges and lead times are measured at `now` — see above.
  const rows = useMemo(
    () =>
      orderTaskRows(
        allRows.filter((row) => matchesListView(row, resolved, { me, now })),
        view.sort,
        now,
      ),
    [allRows, resolved, me, view.sort, now],
  );

  return { allRows, resolved, rows, me };
}
